/**
 * @file ImageModal.js
 * @module src/js/modals/ImageModal
 * @description
 * **Component to display an image in a modal overlay.**
 * - Renders an image centered in the viewport with a darkened, blurred background.
 * - Closes the modal when clicking outside the image.
 * - Ensures the image maintains its aspect ratio and fits within the viewport.
 * - Includes enter/exit animations (fade + pop/shrink), similar to ConfirmWindow.
 *
 * @version
 * <p>
 *  2025-12-02: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Added blur and pop/shrink animations. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../contexts/ThemeProvider";
import { useModal } from "../contexts/ModalProvider";

export default function ImageModal() {
    const { theme } = useTheme();
    const { modalImage, setModalImage } = useModal();
    const [aspectRatio, setAspectRatio] = useState(null); // image width / height
    const [isClosing, setIsClosing] = useState(false); // need this to let the element stay until animation ends

    const enterDuration = 150; // fade in + pop
    const exitDuration = 100;  // fade out + shrink

    const handleContainerClick = useCallback((e) => {
        // If we don't know the aspect ratio yet, treat as clicking on the image
        if (!aspectRatio) {
            e.stopPropagation();
            return;
        }

        const rect = e.currentTarget.getBoundingClientRect();
        const containerWidth = rect.width;
        const containerHeight = rect.height;
        const containerAR = containerWidth / containerHeight;

        let imgWidth, imgHeight, offsetX, offsetY;

        if (aspectRatio > containerAR) {
            // Image is "wider" than container: full width, letterbox top/bottom
            imgWidth = containerWidth;
            imgHeight = containerWidth / aspectRatio;
            offsetX = 0;
            offsetY = (containerHeight - imgHeight) / 2;
        } else {
            // Image is "taller" than container: full height, letterbox left/right
            imgHeight = containerHeight;
            imgWidth = containerHeight * aspectRatio;
            offsetX = (containerWidth - imgWidth) / 2;
            offsetY = 0;
        }

        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        const insideImage =
            clickX >= offsetX &&
            clickX <= offsetX + imgWidth &&
            clickY >= offsetY &&
            clickY <= offsetY + imgHeight;

        if (insideImage) {
            // Treat as image click: don't close
            e.stopPropagation();
        }
        // If NOT insideImage, let it bubble up to the overlay <div> and close the modal
    }, [aspectRatio]);

    const closeWithAnimation = useCallback(() => {
        setIsClosing(true);

        // Wait for the animation to finish before unmounting
        setTimeout(() => {
            setIsClosing(false);
            setModalImage(null);
            setAspectRatio(null);
        }, exitDuration);
    }, [setModalImage]);

    if (!modalImage) return null; // don't render anything if no image to show

    const imagePortalRoot = document.getElementById("image-portal-root");
    if (!imagePortalRoot) return null; // safety guard;

    return createPortal(
        <div
            onClick={closeWithAnimation}
            style={{
                position: "fixed",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 99999,
                backgroundColor:
                    theme === "dark"
                        ? "rgba(255, 255, 255, 0.15)"
                        : "rgba(0, 0, 0, 0.3)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",

                // Fade-in and Fade-out animation
                animation: isClosing
                    ? `modalOverlayFadeOut ${exitDuration}ms ease-out forwards`
                    : `modalOverlayFadeIn ${enterDuration}ms ease-out forwards`,
            }}
        >
            <div
                onClick={handleContainerClick}
                style={{
                    width: "80vw",
                    height: "80vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",

                    // Pop-in and Pop-out animation
                    animation: isClosing
                        ? `modalDialogShrink ${exitDuration}ms ease-out forwards`
                        : `modalDialogPop ${enterDuration}ms ease-out forwards`,
                }}
            >
                <img
                    src={modalImage}
                    alt={modalImage}
                    onLoad={(e) => {
                        const { naturalWidth, naturalHeight } = e.currentTarget;
                        if (naturalWidth && naturalHeight) {
                            setAspectRatio(naturalWidth / naturalHeight);
                        }
                    }}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain", // fully visible, hits width or height
                        display: "block",
                    }}
                />
            </div>

            <span
                style={{
                    color: "lightgray",
                    position: "absolute",
                    bottom: "4vh",
                    width: "100%",
                    textAlign: "center",
                    fontSize: "0.8em",
                    textShadow: "0 0px 14px rgba(0, 0, 0, 0.8)",
                }}
            >
                Click outside the image to close
            </span>
        </div>,
        imagePortalRoot
    );
}